import { useUserRoleName } from "@jugl-web/domain-resources/users/hooks/useUserRoleName";
import { UserCPanelModel, UserRole } from "@jugl-web/rest-api";
import { InteractiveContainer, Popover, Radio } from "@jugl-web/ui-kit";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { selectUserId } from "@web-src/modules/auth/store/authSlice";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { UserRoleChangeAlert } from "../../../UserRoleChangeAlert";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";

export const UserRolePicker: React.FC<{
  cPanelUser: UserCPanelModel;
}> = ({ cPanelUser }) => {
  const { t } = useTranslations();
  const { getRoleName } = useUserRoleName();
  const { isCx } = useAppVariant();

  const roles = useMemo(
    () => [
      {
        role: UserRole.admin,
        emoji: "🚀",
        desc: t({
          id: "cpanel-page.admin-role-description",
          defaultMessage: "Full permission to manage Workspace information",
        }),
      },
      {
        role: UserRole.member,
        emoji: "🤝",
        desc: t({
          id: "cpanel-page.member-role-description",
          defaultMessage:
            "Access to all app features, without permission to manage Workspace",
        }),
      },
      {
        role: UserRole.taskManager,
        emoji: "👩‍💻",
        desc: t({
          id: "cpanel-page.task-manager-role-description",
          defaultMessage:
            "Full access to manage tasks, communications, shared files, calls, and view team member details",
        }),
        isHidden: isCx,
      },
      {
        role: UserRole.taskEmployee,
        emoji: "🙋",
        desc: t({
          id: "cpanel-page.employee-role-description",
          defaultMessage:
            "Access to Tasks, Chats, Drive and People list only, without permission to manage Workspace",
        }),
        isHidden: isCx,
      },
      {
        role: UserRole.chatManager,
        emoji: "👨‍💻",
        desc: t({
          id: "cpanel-page.chat-manager-role-description",
          defaultMessage:
            "Full access to manage communications, shared files, and calls, with permission to view workspace member details",
        }),
        isHidden: isCx,
      },
      {
        role: UserRole.chatEmployee,
        emoji: "🙋‍♀️",
        desc: t({
          id: "cpanel-page.chat-employee-role-description",
          defaultMessage:
            "Limited to viewing and responding in existing chats, calls and accessing shared files",
        }),
        isHidden: isCx,
      },
      {
        role: UserRole.travelLogEmployee,
        emoji: "🗺️",
        desc: t({
          id: "cpanel-page.travel-log-role-description",
          defaultMessage:
            "Limited to logging live location while performing field sales and customer visits",
        }),
        isHidden: isCx,
      },
    ],
    [isCx, t]
  );

  const meId = useSelector(selectUserId);
  const [updatedRole, setUpdatedRole] = useState<UserRole>();
  const isDisabled = cPanelUser.id === meId;

  return (
    <>
      <Popover
        placement="bottom"
        isDisabled={isDisabled}
        renderTrigger={({ Trigger, triggerRef, isOpen }) => (
          <Trigger
            as={InteractiveContainer}
            ref={triggerRef}
            className={cx(
              "flex w-full items-center justify-center gap-0.5 rounded px-2.5 py-2 text-dark-700 transition-colors outline-none hover:bg-grey-100",
              cPanelUser.role === UserRole.admin &&
                "bg-primary-50 hover:bg-primary-50",
              isDisabled &&
                "cursor-default bg-grey-100 text-dark-300 hover:bg-grey-100"
            )}
          >
            <span>{getRoleName(cPanelUser.role)}</span>
            <ArrowIcon
              className={cx("transition-transform", isOpen && "rotate-180")}
            />
          </Trigger>
        )}
        className="jugl__custom-scrollbar flex max-h-[430px] w-[420px] flex-col gap-4 overflow-y-auto rounded-2xl border border-solid border-dark-100 bg-white p-4 shadow-[0_18px_27px_2px_rgba(0,0,0,0.12)]"
      >
        {({ onClose }) => (
          <>
            {roles
              .filter((el) => !el.isHidden || el.role === cPanelUser.role)
              .map((el) => (
                <InteractiveContainer
                  className={cx(
                    "w-full rounded-lg bg-grey-100 p-3 transition-colors hover:bg-grey-200",
                    el.role === cPanelUser.role &&
                      "bg-primary-50 hover:bg-primary-100"
                  )}
                  key={el.role}
                  isDisabled={el.role === cPanelUser.role}
                  onClick={() => {
                    setUpdatedRole(el.role);
                    onClose();
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-secondary leading-[27px] font-medium text-dark">
                      {el.emoji} {getRoleName(el.role)}
                    </span>
                    <Radio isChecked={el.role === cPanelUser.role} readOnly />
                  </div>
                  <div className="mt-1.5 font-secondary text-sm leading-[140%] text-grey-900">
                    {el.desc}
                  </div>
                </InteractiveContainer>
              ))}
          </>
        )}
      </Popover>
      <UserRoleChangeAlert
        isOpen={!!updatedRole}
        onRequestClose={() => setUpdatedRole(undefined)}
        role={updatedRole}
        user={cPanelUser}
      />
    </>
  );
};
